import { LanguageType, ValueObject } from '@nx-smartmonkey/shared/domain';
import { toDayNoon } from '@nx-smartmonkey/shared/helpers';
import moment from 'moment';
import { PlanStatus } from '../plan/PlanStatus';

export const NAME_SUBSTITUTION_CHARACTER_START = `{{`;
export const NAME_SUBSTITUTION_CHARACTER_END = `}}`;
export const defaultNameSubstitutions = [`day`, `day_name`, `month`, `month_name`, `year`];

export type ExecutionDate = `current_day` | `next_day`;

export const validExecutionDate: ExecutionDate[] = [`current_day`, `next_day`];

interface ProjectPlansProps {
  label: string;
  status: PlanStatus;
  execution_date: ExecutionDate;
}

export class ProjectPlans extends ValueObject<ProjectPlansProps> {
  get label(): string {
    return this.props.label;
  }
  set label(label: string) {
    this.props.label = label;
  }
  get status(): PlanStatus {
    return this.props.status;
  }
  set status(status: PlanStatus) {
    this.props.status = status;
  }
  get execution_date(): ExecutionDate {
    return this.props.execution_date;
  }
  set execution_date(execution_date: ExecutionDate) {
    this.props.execution_date = execution_date;
  }
  private constructor(props: ProjectPlansProps) {
    super(props);
  }

  static create(props: ProjectPlansProps): ProjectPlans {
    return new ProjectPlans(props);
  }

  static labelTemplateToValue({
    template,
    date,
    language,
  }: {
    template: string;
    date: Date;
    language?: LanguageType;
  }): string {
    const regexp = new RegExp(`${NAME_SUBSTITUTION_CHARACTER_START}.+?${NAME_SUBSTITUTION_CHARACTER_END}`, `g`);
    const matches = template.match(regexp);

    let parsedBody = template.replace(/\n/g, `<br>`);

    (matches ?? []).forEach((match: any) => {
      const cleanMatchText = match
        .replace(NAME_SUBSTITUTION_CHARACTER_START, ``)
        .replace(NAME_SUBSTITUTION_CHARACTER_END, ``);

      if (defaultNameSubstitutions.includes(cleanMatchText)) {
        const m = moment(date);
        if (language) m.locale(language);

        switch (cleanMatchText) {
          case `day`: {
            parsedBody = parsedBody.replace(match, m.format(`DD`));
            break;
          }
          case `day_name`: {
            parsedBody = parsedBody.replace(match, m.format(`dddd`));
            break;
          }
          case `month`: {
            parsedBody = parsedBody.replace(match, m.format(`MM`));
            break;
          }
          case `month_name`: {
            parsedBody = parsedBody.replace(match, m.format(`MMMM`));
            break;
          }
          case `year`: {
            parsedBody = parsedBody.replace(match, m.format(`yyyy`));
            break;
          }
        }
      }
    });

    return parsedBody;
  }

  defaultStartDateToValue(): Date {
    const now = moment(new Date());
    if (this.execution_date === `next_day`) {
      const tomorrow = toDayNoon(now.add(1, `day`).toDate());
      return tomorrow;
    }
    const today = toDayNoon(now.toDate());
    return today;
  }
}
